import type { SpaceType } from '~/packages/general/variable/spaceTime.types'
import type { GeoItem, StatsItem } from './statsAndGeoToFeatureCollection.util'

type UnitFormat = 'percentage' | string | null | undefined

export type LegendStatsMetrics = {
  min: number
  max: number
  total: number
  totalValueCount: number
  totalNumerator: number
  totalDenominator: number
}

export type StatsSelectionPartition = {
  visibleStatsItems: StatsItem[]
  outsideSelectionStatsItems: StatsItem[]
  hasComparableGeoSelection: boolean
}

function buildGeoKey(geoId: string | number | null | undefined, geoType: string | null | undefined) {
  if (geoId == null || !geoType) {
    return null
  }

  return `${geoId}-${geoType}`
}

export function partitionStatsItemsByGeoSelection({
  statsItems,
  geoItems,
  childrenType,
}: {
  statsItems: StatsItem[]
  geoItems?: GeoItem[] | null
  childrenType: SpaceType
}): StatsSelectionPartition {
  const hasComparableGeoSelection = childrenType !== 'item' && childrenType !== 'custom'

  if (!hasComparableGeoSelection) {
    return {
      visibleStatsItems: statsItems,
      outsideSelectionStatsItems: [],
      hasComparableGeoSelection,
    }
  }

  const geoKeys = new Set((geoItems ?? []).map(item => buildGeoKey(item.id, item.type)).filter(Boolean))

  const visibleStatsItems: StatsItem[] = []
  const outsideSelectionStatsItems: StatsItem[] = []

  for (const item of statsItems) {
    const key = buildGeoKey(item.geoId, item.geoType)
    if (key && geoKeys.has(key)) {
      visibleStatsItems.push(item)
      continue
    }

    outsideSelectionStatsItems.push(item)
  }

  return {
    visibleStatsItems,
    outsideSelectionStatsItems,
    hasComparableGeoSelection,
  }
}

export function summarizeLegendStatsItems({
  statsItems,
  unitFormat,
  visibleGeoItemsCount,
}: {
  statsItems: StatsItem[]
  unitFormat?: UnitFormat
  visibleGeoItemsCount?: number
}): LegendStatsMetrics {
  const values = statsItems.map(item => item.value).filter((value): value is number => Number.isFinite(value))
  const integerTotal = values.reduce((acc, value) => acc + value, 0)

  const totalValueSum = statsItems
    .map(item => item.valueSum)
    .filter((value): value is number => Number.isFinite(value))
    .reduce((acc, value) => acc + Number(value), 0)

  const totalValueCount = statsItems
    .map(item => item.valueCount)
    .filter((value): value is number => Number.isFinite(value))
    .reduce((acc, value) => acc + Number(value), 0)

  const totalNumerator = statsItems
    .map(item => item.numerator)
    .filter((value): value is number => Number.isFinite(value))
    .reduce((acc, value) => acc + value, 0)

  const totalDenominator = statsItems
    .map(item => item.denominator)
    .filter((value): value is number => Number.isFinite(Number(value)))
    .reduce((acc, value) => acc + Number(value), 0)

  const percentageTotal =
    totalDenominator !== 0 ? (totalNumerator / totalDenominator) * 100 : 0

  const total =
    unitFormat === 'percentage'
      ? percentageTotal
      : totalValueCount > 0
        ? totalValueSum / totalValueCount
        : integerTotal

  if (!values.length) {
    return {
      min: 0,
      max: 0,
      total: 0,
      totalValueCount,
      totalNumerator,
      totalDenominator,
    }
  }

  const valuesWithoutEmptyGeo = statsItems
    .filter(item => item.geoId != null)
    .map(item => item.value)
    .filter((value): value is number => Number.isFinite(value))

  const max = valuesWithoutEmptyGeo.length ? Math.max(...valuesWithoutEmptyGeo) : 0
  const min =
    visibleGeoItemsCount && visibleGeoItemsCount !== valuesWithoutEmptyGeo.length
      ? 0
      : valuesWithoutEmptyGeo.length
        ? Math.min(...valuesWithoutEmptyGeo)
        : 0

  return {
    min,
    max,
    total,
    totalValueCount,
    totalNumerator,
    totalDenominator,
  }
}
